import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import contentService from '../services/contentService'
import Loading from '../components/Loading'

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t) }, [onClose])
  const colors = type === 'success'
    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
    : 'bg-red-50 border-red-300 text-red-800'
  return (
    <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg ${colors} max-w-sm`}>
      <i className={`bi ${type === 'success' ? 'bi-check-circle-fill text-emerald-500' : 'bi-exclamation-circle-fill text-red-500'} text-lg flex-shrink-0`}></i>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><i className="bi bi-x text-lg"></i></button>
    </div>
  )
}

// ── Metric Card ───────────────────────────────────────────────────────────────
function MetricCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <i className={`bi ${icon} text-white text-base`}></i>
      </div>
      <div>
        <p className="text-xs text-gray-400 font-medium">{label}</p>
        <p className="text-xl font-bold text-gray-800">{value ?? '—'}</p>
      </div>
    </div>
  )
}

// ── Posts Panel ───────────────────────────────────────────────────────────────
function PostsPanel({ cuenta, onClose }) {
  const [posts, setPosts]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  useEffect(() => {
    setLoading(true); setError(null)
    contentService.getInstagramMetaPosts(cuenta.ig_user_id, 20)
      .then(({ data }) => setPosts(data.posts || []))
      .catch(e => setError(e?.response?.data?.error || 'Error al cargar publicaciones.'))
      .finally(() => setLoading(false))
  }, [cuenta.ig_user_id])

  const totalImpresiones = posts.reduce((s, p) => s + (p.impressions || 0), 0)
  const totalAlcance     = posts.reduce((s, p) => s + (p.reach || 0), 0)
  const totalGuardados   = posts.reduce((s, p) => s + (p.saved || 0), 0)
  const totalLikes       = posts.reduce((s, p) => s + (p.like_count || 0), 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            {cuenta.profile_pic_url ? (
              <img src={cuenta.profile_pic_url} alt="" className="w-9 h-9 rounded-full object-cover"
                onError={e => { e.target.style.display = 'none' }} />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <i className="bi bi-instagram text-white text-sm"></i>
              </div>
            )}
            <div>
              <p className="font-bold text-gray-800">@{cuenta.username}</p>
              <p className="text-xs text-gray-400">{cuenta.nombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <Loading fullScreen={false} mensaje="Cargando" />
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-2">
              <i className="bi bi-exclamation-circle-fill flex-shrink-0 mt-0.5"></i>
              <span>{error}</span>
            </div>
          ) : (
            <>
              {/* Resumen métricas */}
              {posts.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Resumen de las últimas {posts.length} publicaciones
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <MetricCard icon="bi-eye-fill"       label="Impresiones" value={totalImpresiones.toLocaleString()} color="bg-pink-500" />
                    <MetricCard icon="bi-broadcast"      label="Alcance"     value={totalAlcance.toLocaleString()}     color="bg-orange-400" />
                    <MetricCard icon="bi-bookmark-fill"  label="Guardados"   value={totalGuardados.toLocaleString()}   color="bg-purple-500" />
                    <MetricCard icon="bi-heart-fill"     label="Likes"       value={totalLikes.toLocaleString()}       color="bg-red-400" />
                  </div>
                </div>
              )}

              {/* Grid de posts */}
              {posts.length > 0 ? (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Publicaciones recientes</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {posts.map(post => (
                      <a
                        key={post.id}
                        href={post.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative rounded-xl overflow-hidden bg-gray-100 border border-gray-100 hover:border-pink-300 transition-colors"
                      >
                        <div className="aspect-square">
                          {post.media_url ? (
                            <img src={post.media_url} alt="" className="w-full h-full object-cover"
                              onError={e => { e.target.style.display = 'none' }} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
                              <i className="bi bi-image text-gray-300 text-3xl"></i>
                            </div>
                          )}
                        </div>
                        {/* Overlay con métricas */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          <div className="flex gap-3 text-white text-xs font-semibold">
                            <span><i className="bi bi-heart-fill mr-1"></i>{post.like_count ?? 0}</span>
                            <span><i className="bi bi-chat-fill mr-1"></i>{post.comments_count ?? 0}</span>
                          </div>
                          <div className="flex gap-3 text-white text-xs">
                            <span><i className="bi bi-eye-fill mr-1"></i>{post.impressions ?? 0}</span>
                            <span><i className="bi bi-broadcast mr-1"></i>{post.reach ?? 0}</span>
                          </div>
                        </div>
                        {/* Caption badge */}
                        {post.caption && (
                          <div className="px-2 py-1.5 border-t border-gray-50">
                            <p className="text-[10px] text-gray-500 line-clamp-2">{post.caption}</p>
                          </div>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <i className="bi bi-instagram text-4xl block mb-2"></i>
                  <p className="text-sm">No se encontraron publicaciones</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Account Card ──────────────────────────────────────────────────────────────
function AccountCard({ cuenta, onVerPosts }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        {cuenta.profile_pic_url ? (
          <img src={cuenta.profile_pic_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-pink-200"
            onError={e => { e.target.style.display = 'none' }} />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
            <i className="bi bi-instagram text-white text-xl"></i>
          </div>
        )}
        <div className="min-w-0">
          <p className="font-bold text-gray-800 truncate">@{cuenta.username}</p>
          <p className="text-sm text-gray-500 truncate">{cuenta.nombre}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            <i className="bi bi-people-fill mr-1 text-purple-400"></i>
            {cuenta.seguidores?.toLocaleString()} seguidores
          </p>
        </div>
      </div>

      {cuenta.bio && (
        <p className="text-sm text-gray-600 line-clamp-2 border-t border-gray-50 pt-3">{cuenta.bio}</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {['instagram_basic', 'instagram_manage_insights', 'pages_show_list', 'pages_read_engagement'].map(p => (
          <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-medium border border-purple-100">
            <i className="bi bi-check-circle-fill text-purple-400 text-[10px]"></i>
            {p}
          </span>
        ))}
      </div>

      {cuenta.sincronizado_en && (
        <p className="text-[11px] text-gray-400">
          <i className="bi bi-clock mr-1"></i>
          Sincronizado {new Date(cuenta.sincronizado_en).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      )}

      <button
        onClick={() => onVerPosts(cuenta)}
        className="flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm font-semibold rounded-xl transition-all"
      >
        <i className="bi bi-graph-up-arrow"></i>Ver publicaciones y métricas
      </button>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function InstagramConexionPage() {
  const [searchParams]                    = useSearchParams()
  const [estado, setEstado]               = useState(null)
  const [loading, setLoading]             = useState(true)
  const [conectando, setConectando]       = useState(false)
  const [sincronizando, setSincronizando] = useState(false)
  const [desconectando, setDesconectando] = useState(false)
  const [toast, setToast]                 = useState(null)
  const [cuentaPosts, setCuentaPosts]     = useState(null)

  const showToast = (message, type = 'success') => setToast({ message, type })

  const cargarEstado = useCallback(() => {
    setLoading(true)
    contentService.getInstagramMetaEstado()
      .then(({ data }) => setEstado(data))
      .catch(() => showToast('Error al cargar el estado de la conexión', 'error'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (searchParams.get('meta_connected') === '1') showToast('Instagram conectado correctamente ✓')
    if (searchParams.get('meta_error'))              showToast('Error al conectar con Meta. Intenta nuevamente.', 'error')
    cargarEstado()
  }, [cargarEstado, searchParams])

  const handleConectar = async () => {
    setConectando(true)
    try {
      const { data } = await contentService.getMetaOAuthUrl()
      window.location.href = data.url
    } catch {
      showToast('No se pudo obtener la URL de autenticación de Meta', 'error')
      setConectando(false)
    }
  }

  const handleSincronizar = async () => {
    setSincronizando(true)
    try {
      const { data } = await contentService.sincronizarInstagramMeta()
      setEstado(data)
      showToast('Cuentas sincronizadas correctamente')
    } catch {
      showToast('Error al sincronizar cuentas', 'error')
    } finally { setSincronizando(false) }
  }

  const handleDesconectar = async () => {
    if (!window.confirm('¿Desconectar la cuenta de Meta? Se eliminarán todas las cuentas Instagram vinculadas.')) return
    setDesconectando(true)
    try {
      await contentService.desconectarInstagramMeta()
      setEstado({ conectado: false, cuentas: [] })
      showToast('Cuenta Meta desconectada')
    } catch {
      showToast('Error al desconectar', 'error')
    } finally { setDesconectando(false) }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 px-4 sm:px-0 py-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {cuentaPosts && (
        <PostsPanel cuenta={cuentaPosts} onClose={() => setCuentaPosts(null)} />
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center flex-shrink-0">
            <i className="bi bi-instagram text-white text-xl sm:text-2xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">Cuentas de Instagram</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              Conecta tu cuenta de Facebook para vincular tus Instagram Business Accounts
              y acceder a métricas de alcance, impresiones y publicaciones.
            </p>
          </div>
          {estado?.conectado && (
            <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto">
              <button
                onClick={handleSincronizar}
                disabled={sincronizando}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
              >
                <i className={`bi bi-arrow-clockwise ${sincronizando ? 'animate-spin' : ''}`}></i>
                Sincronizar
              </button>
              <button
                onClick={handleDesconectar}
                disabled={desconectando}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-lg transition-colors"
              >
                <i className="bi bi-plug-fill"></i>
                Desconectar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Permisos */}
      <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
          <i className="bi bi-shield-check-fill mr-1"></i>Permisos que utiliza esta integración
        </p>
        <div className="space-y-1.5">
          {[
            ['instagram_basic',           'Lee el perfil e información básica de tus Instagram Business Accounts.'],
            ['instagram_manage_insights', 'Accede a métricas de alcance, impresiones y guardados de tus publicaciones.'],
            ['pages_show_list',           'Lista las Facebook Pages vinculadas a tu cuenta.'],
            ['pages_read_engagement',     'Lee métricas de engagement de tus Facebook Pages.'],
          ].map(([perm, desc]) => (
            <div key={perm} className="flex items-start gap-2 text-sm text-blue-800">
              <i className="bi bi-check-circle-fill text-blue-500 mt-0.5 flex-shrink-0"></i>
              <span><strong>{perm}</strong> — {desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Estado / Cuentas */}
      {loading ? (
        <Loading fullScreen={false} mensaje="Cargando" />
      ) : estado?.conectado ? (
        estado.cuentas.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
            <i className="bi bi-instagram text-gray-300 text-4xl mb-3 block"></i>
            <p className="font-semibold text-gray-700">No se encontraron cuentas Instagram Business</p>
            <p className="text-sm text-gray-400 mt-1 max-w-sm mx-auto">
              Asegúrate de que tu cuenta de Facebook tiene al menos una Page con una Instagram Business Account vinculada.
            </p>
            <button
              onClick={handleSincronizar}
              disabled={sincronizando}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-pink-500 hover:bg-pink-600 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              <i className={`bi bi-arrow-clockwise ${sincronizando ? 'animate-spin' : ''}`}></i>
              Volver a sincronizar
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {estado.cuentas.map(cuenta => (
              <AccountCard
                key={cuenta.ig_user_id}
                cuenta={cuenta}
                onVerPosts={setCuentaPosts}
              />
            ))}
          </div>
        )
      ) : (
        /* Pantalla de conexión */
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center space-y-5">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center mx-auto shadow-lg shadow-pink-200">
            <i className="bi bi-instagram text-white text-4xl"></i>
          </div>
          <div>
            <p className="text-lg font-bold text-gray-800">Conecta con Facebook</p>
            <p className="text-sm text-gray-400 mt-1 max-w-sm mx-auto">
              Autoriza el acceso a tus Facebook Pages para vincular tus cuentas Instagram Business
              y ver métricas de alcance, impresiones y publicaciones.
            </p>
          </div>
          <button
            onClick={handleConectar}
            disabled={conectando}
            className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#1877F2] hover:bg-[#166fe5] text-white font-bold rounded-2xl transition-colors shadow-md shadow-blue-200 text-base"
          >
            {conectando
              ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              : <i className="bi bi-facebook text-xl"></i>}
            Continuar con Facebook
          </button>
          <p className="text-xs text-gray-400">Solo se accede a cuentas Instagram Business vinculadas a tus Facebook Pages.</p>
        </div>
      )}
    </div>
  )
}
